import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { downloadFile, fileUrl } from "../lib/api";
import VerticalVideo from "./VerticalVideo";
import { Download, X, Film, Share2 } from "lucide-react";

const PRESETS = ["TikTok", "Reels", "Shorts"];

export default function Episodes({ episodes, goNewStory }) {
  const [active, setActive] = useState(null);

  if (episodes.length === 0) {
    return (
      <div>
        <Header />
        <div className="glass rounded-3xl p-14 text-center" data-testid="episodes-empty">
          <div className="h-16 w-16 rounded-2xl candy-blueberry grid place-items-center mx-auto mb-4 animate-float">
            <Film className="h-8 w-8 text-white" />
          </div>
          <p className="font-display text-lg">No episodes yet</p>
          <p className="text-sm text-slate-400 mt-1 mb-4">Finished love stories will live here, ready to download.</p>
          <button onClick={goNewStory} className="candy-strawberry rounded-xl px-5 py-2.5 font-semibold text-white" data-testid="create-first-episode-btn">Create your first episode</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Header />
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
        {episodes.map((ep, i) => (
          <motion.button
            key={ep.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
            onClick={() => setActive(ep)}
            className="glass rounded-2xl overflow-hidden text-left group hover:border-[#FF3B60]/50 transition-colors"
            data-testid={`episode-card-${i + 1}`}
          >
            <div className="vframe aspect-[9/16] border-0 rounded-none">
              <video src={fileUrl(ep.final_path)} className="h-full w-full object-cover" muted preload="metadata" />
              <div className="absolute inset-0 grid place-items-center bg-black/20 group-hover:bg-black/10 transition-colors">
                <div className="h-11 w-11 rounded-full candy-strawberry grid place-items-center"><Film className="h-5 w-5 text-white" /></div>
              </div>
            </div>
            <div className="p-3.5">
              <h3 className="font-semibold text-sm truncate">{ep.title}</h3>
              <p className="text-xs text-slate-400 mt-0.5 truncate">{ep.total_clips} clips · {ep.total_clips * 8}s</p>
            </div>
          </motion.button>
        ))}
      </div>

      <AnimatePresence>
        {active && (
          <motion.div className="fixed inset-0 z-50 grid place-items-center p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setActive(null)} />
            <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
              className="glass relative z-10 w-full max-w-sm rounded-3xl p-6" data-testid="episode-modal">
              <button className="absolute right-5 top-5 text-slate-400 hover:text-white z-20" onClick={() => setActive(null)} data-testid="close-episode-modal"><X className="h-5 w-5" /></button>
              <h3 className="font-display text-lg font-semibold mb-1 pr-8">{active.title}</h3>
              <p className="text-xs text-[#FF8A00] mb-4">{active.hook}</p>
              <VerticalVideo path={active.final_path} version={active.updated_at} badge="9:16 · HD" testid="episode-player" />
              <div className="flex gap-2 mt-4 justify-center">
                {PRESETS.map((p) => (
                  <span key={p} className="text-[10px] px-2.5 py-1 rounded-full bg-white/5 text-slate-300 flex items-center gap-1"><Share2 className="h-3 w-3" />{p}</span>
                ))}
              </div>
              <button onClick={() => downloadFile(active.final_path, `${active.title}.mp4`)}
                className="mt-4 w-full candy-lime rounded-xl py-3 font-semibold text-[#0F0E17] flex items-center justify-center gap-2 hover:opacity-90" data-testid="modal-download-btn">
                <Download className="h-4 w-4" /> Download MP4
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function Header() {
  return (
    <div className="mb-6">
      <h2 className="font-display text-2xl sm:text-3xl font-bold">Episodes</h2>
      <p className="text-sm text-slate-400 mt-1">Your finished love stories, ready for the feed.</p>
    </div>
  );
}
